"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.localizedToStandardTime = exports.standardToLocalizedTime = exports.formatDate = exports.formatTime = exports.formatDateTime = exports.localDateFieldFormat = exports.DateFieldValueFormatter = exports.formatStandardDate = void 0;
const moment_1 = __importDefault(require("moment"));
const isString_1 = __importDefault(require("lodash-es/isString"));
const date_fns_1 = require("date-fns");
const intl_1 = require("../locale/intl");
const types_1 = require("@smartsuite/types");
const texts_1 = require("../texts");
const date_1 = require("./mixins/date");
const date_2 = require("../utils/date");
/**
 * Format a string date to 'MMM d, y' format.
 */
const formatStandardDate = (date, locale) => {
    return (0, moment_1.default)(date).locale(locale).format("MMM D, YYYY");
};
exports.formatStandardDate = formatStandardDate;
class DateFieldValueFormatter extends date_1.DateFormatterMixin {
    formatTime(date, userPreferences) {
        const format = userPreferences.locale.time_format === "24h" ? "HH:mm" : "h:mm A";
        return moment_1.default.tz(date, userPreferences.timezone.value).format(format);
    }
    prepareFormat(date, fieldDisplayFormat, userPreferences, dateFormat, keepYear) {
        var _a;
        const momentDate = typeof date === "string" ? (0, moment_1.default)(date) : date;
        let format = dateFormat !== null && dateFormat !== void 0 ? dateFormat : (_a = userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.locale) === null || _a === void 0 ? void 0 : _a.date_format;
        const now = (0, moment_1.default)();
        const isCurrentYear = now.utc().year() === moment_1.default.utc(momentDate).year();
        if (fieldDisplayFormat !== "compact" && isCurrentYear && !keepYear) {
            format = this.removeYearFromFormat(format);
        }
        return (0, exports.localDateFieldFormat)(fieldDisplayFormat, format);
    }
    removeYearFromFormat(dateFormat) {
        var _a, _b;
        const delimiter = (_b = (_a = /[.\-\s/]+/.exec(dateFormat)) === null || _a === void 0 ? void 0 : _a.pop()) !== null && _b !== void 0 ? _b : "";
        const splitFormat = dateFormat.split(delimiter).filter((item) => {
            return !item.includes("Y");
        });
        return splitFormat.join(delimiter);
    }
    formatDate(date, userPreferences, fieldDisplayFormat, dateFormat, keepYear) {
        var _a;
        if (!date || !((_a = userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.locale) === null || _a === void 0 ? void 0 : _a.date_format)) {
            return "";
        }
        const format = this.prepareFormat(date, fieldDisplayFormat, userPreferences, dateFormat, keepYear);
        return date.format(format);
    }
    prepareDateValue(date, userPreferences, includeTime) {
        var _a, _b, _c;
        let value;
        // convert string date to Moment date object
        if ((0, isString_1.default)(date)) {
            value = moment_1.default.utc(date, moment_1.default.ISO_8601);
        }
        else {
            value = date;
        }
        if (!(value instanceof moment_1.default)) {
            return null;
        }
        if ((_a = userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.language) === null || _a === void 0 ? void 0 : _a.value) {
            value = value.locale((_b = userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.language) === null || _b === void 0 ? void 0 : _b.value);
        }
        if (includeTime) {
            value = value.tz((_c = userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.timezone) === null || _c === void 0 ? void 0 : _c.value);
        }
        return value;
    }
    transform(date, userPreferences, fieldDisplayFormat, includeTime, dateFormat, keepYear) {
        if (!date || !userPreferences) {
            return "";
        }
        const value = this.prepareDateValue(date, userPreferences, includeTime);
        if (!value) {
            return "";
        }
        const formattedDate = this.formatDate(value, userPreferences, fieldDisplayFormat, dateFormat, keepYear);
        if (includeTime) {
            const formattedTime = this.formatTime(value, userPreferences);
            return (0, intl_1.$t)(texts_1.dateText.date_at_time, { formattedDate, formattedTime });
        }
        return formattedDate;
    }
}
exports.DateFieldValueFormatter = DateFieldValueFormatter;
/**
 * Makes date format according ordering in user preferences
 * 'YYYY-MM-DD' - year, month, day order
 * 'DD.MM.YYYY' - day, month, year order
 */
const localDateFieldFormat = (fieldFormat, userFormat, formatFor = "moment") => {
    const MOMENT_FORMATS_MAPPING = {
        $monthDay: {
            calendar: {
                standard: "d",
                compact: "d",
                long: "d",
                full: "d",
            },
            moment: {
                standard: "D",
                compact: "D",
                long: "D",
                full: "D",
            },
        },
        $month: {
            calendar: {
                standard: "M",
                compact: "m",
                long: "M",
                full: "D",
            },
            moment: {
                standard: "MMM",
                compact: "M",
                long: "MMM",
                full: "MMMM",
            },
        },
        $year: {
            calendar: {
                standard: "yy",
                compact: "yy",
                long: "yy",
                full: "yy",
            },
            moment: {
                standard: "YYYY",
                compact: "YYYY",
                long: "YYYY",
                full: "YYYY",
            },
        },
        $weekDay: {
            calendar: {
                standard: "D",
                compact: "D",
                long: "D",
                full: "ddd",
            },
            moment: {
                standard: "ddd",
                compact: "ddd",
                long: "ddd",
                full: "dddd",
            },
        },
    };
    return getPseudoFormat(fieldFormat, userFormat)
        .replace("$monthDay", MOMENT_FORMATS_MAPPING.$monthDay[formatFor][fieldFormat])
        .replace("$month", MOMENT_FORMATS_MAPPING.$month[formatFor][fieldFormat])
        .replace("$year", MOMENT_FORMATS_MAPPING.$year[formatFor][fieldFormat])
        .replace("$weekDay", MOMENT_FORMATS_MAPPING.$weekDay[formatFor][fieldFormat]);
};
exports.localDateFieldFormat = localDateFieldFormat;
const getPseudoFormat = (fieldDisplayFormat, userPreferencesFormat) => {
    const parts = userPreferencesFormat.split(/[.\-\s/]+/);
    const order = parts
        .map((val) => val.toLowerCase()[0])
        .map((val) => ({ y: "$year", m: "$month", d: "$monthDay" })[val]);
    if (fieldDisplayFormat === "compact") {
        let result = userPreferencesFormat;
        userPreferencesFormat.split(/[.\-\s/]+/).forEach((str, idx) => {
            var _a;
            const replaceWith = (_a = order[idx]) !== null && _a !== void 0 ? _a : "";
            result = result.replace(str, replaceWith);
        });
        return result;
    }
    else {
        let format;
        if (order.includes("$year")) {
            format =
                order.indexOf("$year") === 0
                    ? "$year, " + order.slice(1).join(" ")
                    : order.slice(0, -1).join(" ") + ", $year";
        }
        else {
            format = order.join(" ");
        }
        return ["long", "full"].includes(fieldDisplayFormat)
            ? `$weekDay, ${format}`
            : format;
    }
};
const formatDateTime = (date, includeTime, userPreferences, dateFormat = "standard") => {
    if (!date || !userPreferences) {
        return "";
    }
    const formattedDate = (0, exports.formatDate)(date, dateFormat, userPreferences);
    if (includeTime) {
        const formattedTime = (0, exports.formatTime)(date, userPreferences);
        return (0, intl_1.$t)(texts_1.createUpdateFieldText.date_at_time, {
            formattedDate,
            formattedTime,
        });
    }
    return formattedDate;
};
exports.formatDateTime = formatDateTime;
const formatTime = (date, userPreferences) => {
    if (!userPreferences) {
        return "";
    }
    const dateObj = (0, isString_1.default)(date)
        ? (0, date_2.toMomentDate)(date, userPreferences.locale.value, userPreferences.timezone.value, true)
        : date;
    const format = (0, date_2.getTimeFormat)(userPreferences.locale.time_format);
    return dateObj
        ? moment_1.default.tz(dateObj, userPreferences.timezone.value).format(format)
        : "";
};
exports.formatTime = formatTime;
const formatDate = (date, dateFormat, userPreferences) => {
    var _a, _b, _c;
    let dateObj = (0, isString_1.default)(date)
        ? (0, date_2.toMomentDate)(date, userPreferences.locale.value, userPreferences.timezone.value, true)
        : date;
    dateObj = dateObj || moment_1.default.utc();
    if ((_a = userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.language) === null || _a === void 0 ? void 0 : _a.value) {
        dateObj = dateObj.locale((_b = userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.language) === null || _b === void 0 ? void 0 : _b.value);
    }
    const format = (0, exports.localDateFieldFormat)(dateFormat, (_c = userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.locale) === null || _c === void 0 ? void 0 : _c.date_format);
    return dateObj.format(format);
};
exports.formatDate = formatDate;
/**
 * Convert localized time (e.g. 1:30 AM) to standard time (e.g. 01:30:00)
 */
function standardToLocalizedTime(standardTime, preferences) {
    var _a;
    const date = (_a = (0, date_2.parseTime)(standardTime)) !== null && _a !== void 0 ? _a : 0;
    return (0, date_fns_1.format)(date, (0, date_2.getTimeMaskFromUserPreferences)(preferences));
}
exports.standardToLocalizedTime = standardToLocalizedTime;
/**
 * Convert standard time (e.g. 01:30:00) to localized time (e.g. 1:30 AM)
 */
function localizedToStandardTime(localizedTime) {
    var _a;
    const date = (_a = (0, date_2.parseTime)(localizedTime)) !== null && _a !== void 0 ? _a : 0;
    return (0, date_fns_1.format)(date, types_1.TIME_VALUE_FORMAT);
}
exports.localizedToStandardTime = localizedToStandardTime;
